/**
 * MUTAGIM.SHOP — Category Page (Phase 6)
 * Page catégorie avec filtres et produits
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Loader2, X, SlidersHorizontal, Grid, List, ChevronRight } from 'lucide-react';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { ProductCardNetflix } from '../components/product/ProductCardNetflix';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../components/ui/sheet';
import { cn } from '../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function CategoryPage() {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = i18n.language;
  
  const [category, setCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({ sizes: [], colors: [] });
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  // Paramètres
  const size = searchParams.get('size') || '';
  const color = searchParams.get('color') || '';
  const minPrice = searchParams.get('min_price') || '';
  const maxPrice = searchParams.get('max_price') || '';
  const sort = searchParams.get('sort') || 'newest';
  const page = parseInt(searchParams.get('page') || '1', 10);

  // Charger la catégorie et ses produits
  const loadCategory = useCallback(async () => {
    try {
      setLoading(true);
      
      const params = { page, limit: 24, sort };
      if (size) params.size = size;
      if (color) params.color = color;
      if (minPrice) params.min_price = parseFloat(minPrice);
      if (maxPrice) params.max_price = parseFloat(maxPrice);
      
      const response = await axios.get(`${API_URL}/api/storefront/category/${slug}`, { params });
      
      setCategory(response.data.category);
      setProducts(response.data.products || []);
      setTotal(response.data.total || 0);
      setPages(response.data.pages || 1);
      setFilters(response.data.filters || { sizes: [], colors: [] });
      
    } catch (err) {
      console.error('Category load error:', err);
    } finally {
      setLoading(false);
    }
  }, [slug, size, color, minPrice, maxPrice, sort, page]);

  useEffect(() => {
    loadCategory();
  }, [loadCategory]);

  // Mettre à jour un paramètre
  const updateParam = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    if (key !== 'page') {
      newParams.set('page', '1');
    }
    setSearchParams(newParams);
  };

  // Effacer tous les filtres
  const clearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const activeFiltersCount = [size, color, minPrice, maxPrice].filter(Boolean).length;

  const sortOptions = [
    { value: 'newest', label: t('search.sort.newest', 'Nouveautés') },
    { value: 'popularity', label: t('search.sort.popularity', 'Popularité') },
    { value: 'price_asc', label: t('search.sort.priceAsc', 'Prix croissant') },
    { value: 'price_desc', label: t('search.sort.priceDesc', 'Prix décroissant') },
    { value: 'rating', label: t('search.sort.rating', 'Meilleures notes') },
  ];

  const categoryName = category?.name?.[lang] || category?.name?.fr || slug;
  const categoryEmoji = category?.emoji || '📦';

  return (
    <div className="min-h-screen bg-mutagim-dark" data-testid="category-page">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Breadcrumb amélioré */}
        <nav className="flex items-center text-sm text-gray-400 mb-6" data-testid="category-breadcrumb">
          <Link to="/" className="hover:text-white transition-colors">{t('nav.home', 'Accueil')}</Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <Link to="/categories" className="hover:text-white transition-colors">{t('nav.categories', 'Catégories')}</Link>
          <ChevronRight className="w-4 h-4 mx-2" />
          <span className="text-white font-medium flex items-center gap-1">
            {categoryEmoji && <span>{categoryEmoji}</span>}
            {categoryName}
          </span>
        </nav>
        
        {/* Titre catégorie */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white flex items-center gap-3">
            <span className="text-4xl">{categoryEmoji}</span>
            <span>{categoryName}</span>
          </h1>
          {category?.description?.[lang] && (
            <p className="text-gray-400 mt-2 max-w-2xl">
              {category.description[lang]}
            </p>
          )}
          <p className="text-gray-500 mt-2">
            {total} {t('search.productsFound', 'produits')}
          </p>
        </div>
        
        {/* Barre de filtres */}
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6 pb-6 border-b border-gray-800">
          <div className="flex items-center gap-2 flex-wrap">
            {/* Mobile filtres */}
            <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
              <SheetTrigger asChild>
                <Button variant="outline" className="border-gray-700 text-white lg:hidden">
                  <SlidersHorizontal className="w-4 h-4 mr-2" />
                  {t('search.filters', 'Filtres')}
                  {activeFiltersCount > 0 && (
                    <Badge className="ml-2 bg-mutagim-red">{activeFiltersCount}</Badge>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-mutagim-dark border-gray-800 w-80">
                <SheetHeader>
                  <SheetTitle className="text-white">{t('search.filters', 'Filtres')}</SheetTitle>
                </SheetHeader>
                <CategoryFilterPanel 
                  filters={filters}
                  size={size}
                  color={color}
                  minPrice={minPrice}
                  maxPrice={maxPrice}
                  updateParam={updateParam}
                  clearFilters={clearFilters}
                  t={t}
                />
              </SheetContent>
            </Sheet>
            
            {/* Filtres actifs */}
            {size && (
              <Badge variant="secondary" className="bg-gray-800 text-white">
                {t('search.size', 'Taille')}: {size}
                <button onClick={() => updateParam('size', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            {color && (
              <Badge variant="secondary" className="bg-gray-800 text-white">
                {t('search.color', 'Couleur')}: {color}
                <button onClick={() => updateParam('color', '')} className="ml-1">
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            )}
            
            {activeFiltersCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm"
                className="text-gray-400 hover:text-white"
                onClick={clearFilters}
              >
                {t('search.clearAll', 'Tout effacer')}
              </Button>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            <Select value={sort} onValueChange={(v) => updateParam('sort', v)}>
              <SelectTrigger className="w-[180px] bg-gray-900 border-gray-700 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-gray-900 border-gray-700">
                {sortOptions.map(opt => (
                  <SelectItem 
                    key={opt.value} 
                    value={opt.value}
                    className="text-white hover:bg-gray-800"
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="hidden md:flex border border-gray-700 rounded-lg overflow-hidden">
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "rounded-none",
                  viewMode === 'grid' ? "bg-gray-800 text-white" : "text-gray-400"
                )}
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "rounded-none",
                  viewMode === 'list' ? "bg-gray-800 text-white" : "text-gray-400"
                )}
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
        
        {/* Contenu */}
        <div className="flex gap-8">
          {/* Sidebar Desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <CategoryFilterPanel 
              filters={filters}
              size={size}
              color={color}
              minPrice={minPrice}
              maxPrice={maxPrice}
              updateParam={updateParam}
              clearFilters={clearFilters}
              t={t}
            />
          </aside>
          
          {/* Grille */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-mutagim-red" />
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20">
                <p className="text-gray-400 text-lg mb-4">
                  {t('search.noResults', 'Aucun produit trouvé dans cette catégorie')}
                </p>
                <Button onClick={clearFilters} variant="outline" className="border-gray-700 text-white">
                  {t('search.clearFilters', 'Effacer les filtres')}
                </Button>
              </div>
            ) : (
              <>
                <div className={cn(
                  "grid gap-4",
                  viewMode === 'grid' 
                    ? "grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
                    : "grid-cols-1"
                )}>
                  {products.map((product) => (
                    <ProductCardNetflix 
                      key={product.id} 
                      product={product}
                    />
                  ))}
                </div>
                
                {/* Pagination */}
                {pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-8">
                    <Button
                      variant="outline"
                      className="border-gray-700 text-white"
                      disabled={page <= 1}
                      onClick={() => updateParam('page', String(page - 1))}
                    >
                      {t('common.previous', 'Précédent')}
                    </Button>
                    <span className="text-gray-400 px-4">
                      {page} / {pages}
                    </span>
                    <Button
                      variant="outline"
                      className="border-gray-700 text-white"
                      disabled={page >= pages}
                      onClick={() => updateParam('page', String(page + 1))}
                    >
                      {t('common.next', 'Suivant')}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}

function CategoryFilterPanel({ filters, size, color, minPrice, maxPrice, updateParam, clearFilters, t }) {
  return (
    <div className="space-y-6 py-4">
      {/* Tailles */}
      {filters.sizes?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">{t('search.size', 'Taille')}</h3>
          <div className="flex flex-wrap gap-2">
            {filters.sizes.map((s) => (
              <button
                key={s}
                className={cn(
                  "px-3 py-1 rounded border text-sm transition-all",
                  size === s
                    ? "border-mutagim-red bg-mutagim-red/20 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                )}
                onClick={() => updateParam('size', size === s ? '' : s)}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Couleurs */}
      {filters.colors?.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-white font-medium">{t('search.color', 'Couleur')}</h3>
          <div className="flex flex-wrap gap-2">
            {filters.colors.map((c) => (
              <button
                key={c}
                className={cn(
                  "px-3 py-1 rounded border text-sm transition-all",
                  color === c
                    ? "border-mutagim-red bg-mutagim-red/20 text-white"
                    : "border-gray-700 text-gray-300 hover:border-gray-500"
                )}
                onClick={() => updateParam('color', color === c ? '' : c)}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      )}
      
      {/* Prix */}
      <div className="space-y-3">
        <h3 className="text-white font-medium">{t('search.priceRange', 'Fourchette de prix')}</h3>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            placeholder="Min"
            value={minPrice}
            onChange={(e) => updateParam('min_price', e.target.value)}
            className="bg-gray-800 border-gray-700 text-white w-24"
          />
          <span className="text-gray-400">-</span>
          <Input
            type="number"
            placeholder="Max"
            value={maxPrice}
            onChange={(e) => updateParam('max_price', e.target.value)}
            className="bg-gray-800 border-gray-700 text-white w-24"
          />
        </div>
      </div>
      
      <Button 
        variant="outline" 
        className="w-full border-gray-700 text-white"
        onClick={clearFilters}
      >
        {t('search.clearAll', 'Effacer les filtres')}
      </Button>
    </div>
  );
}

export default CategoryPage;
